import { Grid } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { useEffect, useMemo, useState } from 'react'
import { useFulfillmentCapabilitiesRGD } from 'hooks/store-fulfillment/useFulfillmentCapabilities'
import { useEssentials } from 'hooks/utils/useEssentials'
import './capabilities-store-level.scss'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { translateTableData } from 'locales/utils'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import {
    addHyperlinkToFirstColumn,
    buildDataTableObject,
    DataTableObjectType,
} from 'utils/data'
import {
    buildOrderedLocations,
    buildRgdTableData,
    RgdCapabilitiesData,
    sortChildRows,
} from './helpers'

export const StoreFulfillmentCapabilitiesRGD = () => {
    const { env, session, currentLocation, t, isEnglish } = useEssentials()
    const storeFulfillment = TRANSLATION_KEYS.storeFulfillment
    const { store } = storeFulfillment.inf
    const headings = useMemo(
        () => TRANSLATION_KEYS.storeFulfillment.headings,
        [],
    )
    const {
        data,
        calledAt,
        isCalledAtToday,
        isLoading,
        isError,
        error,
        refetch,
    } = useFulfillmentCapabilitiesRGD({
        env,
        accessToken: session?.accessToken ?? '',
        locationId: currentLocation,
    })

    const rgdData = useMemo(() => (data ?? {}) as RgdCapabilitiesData, [data])
    const rows = useMemo(() => buildOrderedLocations(rgdData), [rgdData])
    const time = isCalledAtToday
        ? `${t(storeFulfillment.timePeriods.today)} ${t(store.asOf)} ${calledAt}`
        : `${t(store.asOf)} ${calledAt}`
    const [sortDirection, setSortDirection] = useState<'asc' | 'dsc' | 'none'>(
        'none',
    )
    const [sortColumnIndex, setSortColumnIndex] = useState<number>(0)

    const childLocationIds = useMemo(
        () =>
            new Set((rgdData.child_locations ?? []).map((c) => c.location_id)),
        [rgdData.child_locations],
    )

    const baseTableData = useMemo(() => {
        const headers = [
            'location',
            'pickup',
            'groceryPickup',
            'driveUp',
            'driveUpReturns',
            'driveUpStarbucks',
            'parkingLotDeliveryPause',
            'sfs',
            'ndd',
            'sdd',
        ]
        let tableData = buildRgdTableData(rows, headers)
        tableData = translateTableData({
            t,
            table: tableData,
            translation: headings,
        })
        return addHyperlinkToFirstColumn(tableData, currentLocation)
    }, [rows, t, headings, currentLocation])

    const tableData = useMemo(() => {
        if (sortDirection === 'none' || sortColumnIndex <= 0) {
            return baseTableData
        }
        return sortChildRows(
            baseTableData,
            childLocationIds,
            sortColumnIndex,
            sortDirection,
        )
    }, [baseTableData, sortDirection, sortColumnIndex, childLocationIds])

    useEffect(() => {
        setSortDirection('none')
        setSortColumnIndex(0)
    }, [rows])

    const columnSortSettingsByIndex = useMemo(() => {
        const sortableColumns = Array.from({ length: 10 }, (_, index) => {
            if (index === 0) {
                return { [index]: { sortable: false } }
            }
            return {
                [index]: {
                    sortable: true,
                    sortType: 'numeric' as const,
                    onSort: (dir: 'asc' | 'dsc') => {
                        setSortDirection(dir)
                        setSortColumnIndex(index)
                    },
                },
            }
        })
        return sortableColumns.reduce(
            (acc, curr) => ({ ...acc, ...curr }),
            {},
        ) as NonNullable<DataTableObjectType['columnSortSettingsByIndex']>
    }, [])

    const widths = useMemo(
        () =>
            isEnglish
                ? [65, 80, 90, 80, 80, 85, 90, 70, 70, 70]
                : [65, 80, 90, 80, 100, 85, 140, 70, 70, 70],
        [isEnglish],
    )

    const dataTableObject = useMemo(
        () =>
            buildDataTableObject({
                currentData: tableData,
                widths,
                isLoading,
                pinFirstColumn: true,
                columnSortSettingsByIndex,
            }),
        [tableData, isLoading, columnSortSettingsByIndex, widths],
    )

    if (isLoading) {
        return (
            <div className="placeholder-container">
                <BasicPlaceholder type="text" rows={1} columns={1} />
                <BasicPlaceholder type="table" rows={10} columns={1} />
            </div>
        )
    }

    if (isError) {
        return <ErrorAlert error={error} onRetry={() => refetch()} />
    }

    if (!isError && rows.length === 0) {
        return <ErrorAlert variant="no-data" />
    }

    return (
        <Grid.Container className="hc8-pt-4x">
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <p className="hc8-fs-050 hc8-ma-none">{time}</p>
            </Grid.Item>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <Table
                    data={dataTableObject}
                    name="store-fulfillment-capabilities-rgd"
                    alternateRowColor={true}
                    scrollableWidth={true}
                ></Table>
            </Grid.Item>
        </Grid.Container>
    )
}
